import { zodResolver } from '@hookform/resolvers/zod'
import { useForm, type DefaultValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import DateRangePicker from '@/components/DateRangePicker/DateRangePicker'
import FormField from '@/components/FormField'
import Input from '@/components/Input/Input'
import {
  FormButtons,
  FormLayout,
  FormSection
} from '@/components/common/FormGrid/FormGrid'
import useExternalErrors from '@/hooks/useExternalErrors'
import type { FormError } from '@/types/form-error'

import {
  schoolBreakPayload,
  type SchoolBreakFormPayload
} from '../constants/school-break-payload'

type SchooBreakFormProps = {
  defaultValues: DefaultValues<SchoolBreakFormPayload>
  errors?: FormError<SchoolBreakFormPayload>[]
  onSubmit: (data: SchoolBreakFormPayload) => void
}

const SchooBreakForm = (props: SchooBreakFormProps) => {
  const { t } = useTranslation('schoolSettings')

  const form = useForm<SchoolBreakFormPayload>({
    resolver: zodResolver(schoolBreakPayload),
    mode: 'all',
    defaultValues: props.defaultValues
  })

  useExternalErrors(props.errors, form)

  return (
    <FormLayout form={form} onSubmit={props.onSubmit}>
      <FormSection header={t('header.school-break')}>
        <FormField
          id="name"
          name="name"
          label={t('label.name')}
          control={form.control}
          required
          render={({ inputProps }) => (
            <Input
              {...inputProps}
              placeholder={t('placeholder.type-school-break-name')}
            />
          )}
        />
        <FormField
          control={form.control}
          id="break-duration"
          label={t('label.break-duration')}
          required
          name="dates"
          render={({ inputProps }) => (
            <DateRangePicker
              {...inputProps}
              onChange={value => {
                if (value) inputProps.onChange?.(value)
              }}
              clearable
              onClear={() => {
                inputProps.onChange?.({})
              }}
            />
          )}
        />
      </FormSection>

      <FormButtons />
    </FormLayout>
  )
}

export default SchooBreakForm
